import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './supabaseClient.js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [applicatorId, setApplicatorId] = useState(null);

  const fetchProfile = useCallback(async (userId) => {
    const [profileRes, applicatorRes] = await Promise.all([
      supabase.from('user_profiles').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('applicators').select('id').eq('user_id', userId).maybeSingle(),
    ]);

    if (profileRes.error) console.error('Failed to fetch profile:', profileRes.error.message);
    if (applicatorRes.error) console.error('Failed to fetch applicator:', applicatorRes.error.message);

    setProfile(profileRes.data || null);
    setApplicatorId(applicatorRes.data?.id || null);
  }, []);

  // Fetch profile whenever user changes (separate from auth listener to avoid Supabase lock deadlock)
  useEffect(() => {
    if (!user) return;
    fetchProfile(user.id);
  }, [user, fetchProfile]);

  useEffect(() => {
    if (!supabase) {
      console.error('Supabase client not initialized — check env vars');
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch((err) => {
      console.error('Failed to get session:', err);
      setLoading(false);
    });

    // Must NOT be async — Supabase calls this inside an internal lock,
    // and awaiting any Supabase query here would deadlock.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      if (!sessionUser) {
        setProfile(null);
        setApplicatorId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    setProfile(null);
    setApplicatorId(null);
    await supabase.auth.signOut();
  };

  const value = useMemo(() => {
    const role = profile?.role || null;
    return {
      user,
      loading,
      signIn,
      signOut,
      profile,
      role,
      isAdmin: role === 'admin',
      isApplicator: !!(profile?.is_applicator && applicatorId),
      applicatorId,
    };
  }, [user, loading, profile, applicatorId]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
